import React from 'react';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';

import { parseShortDateTime, twelveHourTimeFormat, parseShortDateWithTime } from '../../../../utils/dates';
import recurrenceTypes from '../../../../settings/constants/recurrenceTypes';
import { TreatmentRecurrenceSlots } from './TreatmentRecurrenceSlots';
import { selectionTypes } from '../../../../enums/treatmentSelectionType';
import { MonthOption } from '../../../../enums/monthOption';

export default function TreatmentDetails({
  treatmentInstance,
  treatment,
  lastVisitedInfo,
  canEdit,
  includeTreatments,
  refreshGrid,
  isAuditDetailsRender,
}) {
  const selectionType = () => {
    if (treatmentInstance && treatmentInstance.isSpecific) return selectionTypes.specific;
    if (treatmentInstance && treatmentInstance.isMultiple) return selectionTypes.multiple;
    return selectionTypes.timeSlot;
  };
  const isMonthlyReccurence = treatment.recurrenceType === recurrenceTypes.MONTHLY;
  const treatmentDetailsRender = () => {
    return (
      <React.Fragment>
        {!isAuditDetailsRender && (
          <div className="clinician-info-box">
            <div>
              <Typography variant="h6" gutterBottom>
                Last Visited
              </Typography>
              <List className="list-underline">
                <ListItem>
                  <ListItemText primary="Clinician name" />
                  <span className="divider" />
                  <ListItemText secondary={lastVisitedInfo.clinicianName || '-'} />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Date" />
                  <span className="divider" />
                  <ListItemText secondary={parseShortDateWithTime(lastVisitedInfo.lastVisitedTime)} />
                </ListItem>
              </List>
            </div>
          </div>
        )}
        <div className="clinician-info-box">
          <div>
            {treatment.description !== '' && (
              <div className="clinician-info-box">
                <Typography variant="h6" gutterBottom>
                  Treatment Description
                </Typography>
                <div className="treatment-description">{treatment.description}</div>
              </div>
            )}
          </div>
        </div>
        <div className="clinician-info-box">
          <div>
            {!isAuditDetailsRender && treatmentInstance.notes && treatmentInstance.notes !== '' && (
              <div className="clinician-info-box">
                <Typography variant="h6" gutterBottom>
                  Treatment Instance Notes
                </Typography>
                <Box component="div" overflow="auto" maxHeight={150} className="treatment-description">
                  {treatmentInstance.notes}
                </Box>
              </div>
            )}
          </div>
        </div>
        <div className="clinician-info-box">
          <Typography variant="h6" gutterBottom>
            Treatment details
          </Typography>
          <List className="list-underline">
            <ListItem>
              <ListItemText primary="Date from" />
              <span className="divider" />
              <ListItemText secondary={parseShortDateTime(treatment.dateFrom)} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Date to" />
              <span className="divider" />
              <ListItemText secondary={parseShortDateTime(treatment.dateTo)} />
            </ListItem>
            {selectionType() === selectionTypes.specific && (
              <ListItem>
                <ListItemText primary="Treatment start" />
                <span className="divider" />
                <ListItemText secondary={twelveHourTimeFormat(treatment.specificTimeFrom)} />
              </ListItem>
            )}
            <ListItem>
              <ListItemText primary="Review date" />
              <span className="divider" />
              <ListItemText secondary={parseShortDateTime(treatment.reviewDate)} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Recurrence type" />
              <span className="divider" />
              <ListItemText secondary={treatment.recurrenceType} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Recur Every" />
              <span className="divider" />
              <ListItemText secondary={treatment.recurEvery} />
            </ListItem>
            {isMonthlyReccurence && (
              <ListItem>
                <ListItemText primary="Monthly Recurrence type" />
                <span className="divider" />
                <ListItemText secondary={MonthOption[treatment.monthlyRecurrenceType]} />
              </ListItem>
            )}
            {isMonthlyReccurence && (
              <ListItem>
                <ListItemText primary={MonthOption[treatment.monthlyRecurrenceType]} />
                <span className="divider" />
                <ListItemText
                  secondary={
                    MonthOption[treatment.monthlyRecurrenceType] === MonthOption.DayOfTheMonth
                      ? parseInt(Object.keys(treatment.selectedDays)[0], 10) + 1
                      : treatment.selectedWeeks[0]
                  }
                />
              </ListItem>
            )}
            {isMonthlyReccurence && MonthOption[treatment.monthlyRecurrenceType] === MonthOption.WeekOfTheMonth && (
              <ListItem>
                <ListItemText primary="Selected Day" />
                <span className="divider" />
                <ListItemText secondary={treatment.selectedWeekDays[0]} />
              </ListItem>
            )}
            <ListItem>
              <ListItemText primary="Required Clinicians" />
              <span className="divider" />
              <ListItemText secondary={treatment.numberOfClinicians} />
            </ListItem>
            {!isAuditDetailsRender && (
              <ListItem>
                <ListItemText primary="Additional Clinicians" />
                <span className="divider" />
                <ListItemText secondary={treatment.additionalClinician} />
              </ListItem>
            )}
            <ListItem>
              <ListItemText primary="Original Unit" />
              <span className="divider" />
              <ListItemText secondary={treatment.orgUnit.name} />
            </ListItem>
          </List>
        </div>
      </React.Fragment>
    );
  };

  const treatmentAddressRender = () => {
    return (
      <div className="clinician-info-box">
        <Typography variant="h6" gutterBottom>
          Alternative Address
        </Typography>
        <List className="list-underline">
          <ListItem>
            <ListItemText primary="Address Description" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.description || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Address line 1" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.addressLine1 || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Address line 2" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.addressLine2 || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Address line 3" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.addressLine3 || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Town City" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.townCity || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Post Code" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.postCode || '-'} />
          </ListItem>
          <ListItem>
            <ListItemText primary="Access Details" />
            <span className="divider" />
            <ListItemText secondary={treatment.address.accessDetails || '-'} />
          </ListItem>
        </List>
      </div>
    );
  };

  const personaliseTreatment = () => {
    return (
      <div className="clinician-info-box">
        <div>
          <Typography variant="h6" gutterBottom>
            Personalise Treatment
          </Typography>
          <List className="list-underline">
            <ListItem>
              <ListItemText primary="Overridden RAG status" />
              <span className="divider" />
              <ListItemText
                secondary={treatment.overriddenRagStatus != null ? treatment.overriddenRagStatus.name : '-'}
              />
            </ListItem>
            <ListItem>
              <ListItemText primary="Overridden Required Band" />
              <span className="divider" />
              <ListItemText secondary={treatment.overriddenBand != null ? treatment.overriddenBand.name : '-'} />
            </ListItem>
            <ListItem>
              <ListItemText primary="Preferred Clinician" />
              <span className="divider" />
              <ListItemText
                secondary={treatment.preferredClinician != null ? treatment.preferredClinician.clinicianFullName : '-'}
              />
            </ListItem>
          </List>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {treatmentDetailsRender()}
      {treatment.address && treatmentAddressRender()}
      {personaliseTreatment()}
      {!isAuditDetailsRender && (
        <TreatmentRecurrenceSlots
          treatment={treatment}
          treatmentInstance={treatmentInstance}
          canEdit={canEdit}
          includeTreatments={includeTreatments}
          refreshGrid={refreshGrid}
        />
      )}
    </React.Fragment>
  );
}
